import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { APIGetQualificationLabs } from 'utils/api';
import LoadingImage from 'components/LoadingImage';
import { Popover } from '@mui/material';
import { isImageValid } from 'utils/utils';

const defaultLabIcon = require("assets/default-lab-icon.svg").default;

// --- Reusable Tag Component ---
interface TagProps {
  icon?: string;
  text: string;
  bgColor?: string;
  textColor?: string;
}

const Tag: React.FC<TagProps> = ({
  icon,
  text,
  bgColor = 'bg-blue-100',
  textColor = 'text-gray-900',
}) => {
  return (
    <div
      className={`inline-flex items-center gap-1.5 ${bgColor} ${textColor} rounded-lg px-3 py-1.5 text-sm custom-text-[poppins-400-12px]`}
    >
      {icon && <span>{icon}</span>}
      <span className="whitespace-nowrap">{text}</span>
    </div>
  );
};

interface QualificationCardProps {
  lab: any;
  onSendInvite: (lab: any) => void;
  onShowDetails: (lab: any) => void;
}

const QualificationCard: React.FC<QualificationCardProps> = ({
  lab,
  onSendInvite,
  onShowDetails,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const popoverButtonRef = useRef<HTMLButtonElement>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? `lab-popover-${lab.id}` : undefined;

  const getRateTag = (rate?: number) => {
    // if (rate === undefined || rate === null) return { icon: "⭐", text: "5" };
    // if (rate <= 3) return { icon: "⭐", text: "Easy" };
    // if (rate <= 7) return { icon: "⭐", text: "Medium" };
    if (rate === undefined || rate === null) return { icon: "⭐", text: "5" };
    return { icon: "⭐", text: rate.toString() };
  };

  const rateTag = getRateTag(lab._rates);

  return (
    <>
      <div className="relative flex flex-col md:flex-row gap-4 md:gap-6 bg-white rounded-2xl shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] p-5 md:p-6 w-full hover:shadow-[0px_7px_29px_0px_rgba(100,100,111,0.30)] transition-shadow duration-300 cursor-pointer" onClick={() => { if (!open) onShowDetails(lab); }}>
        <div className="flex overflow-hidden flex-shrink-0 justify-center items-center self-center w-24 h-24 rounded-lg md:w-28 md:h-28 md:self-start">
          <img src={
            isImageValid(lab.illustration)
              ? lab.illustration
              : defaultLabIcon
          } alt={lab.name || 'Lab'} className="object-cover w-full h-full" />
        </div>

        <div className="flex flex-col flex-grow">
          <div className="flex justify-between items-start mb-2">
            <h3 className="custom-text-[poppins-600-18px] text-gray-800 leading-7 mr-2">
              {lab.name || "Untitled Lab"}
            </h3>
            <div className="relative z-10 flex-shrink-0">
              <button
                ref={popoverButtonRef}
                aria-describedby={id}
                onClick={handleOpenPopover}
                className="p-1 text-gray-500 pointer-events-auto hover:text-blue"
              >
                <img src={require("assets/more-dots-icon.svg").default} alt="More options" className="w-5 h-5" />
              </button>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    width: '160px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                    marginTop: '8px',
                  },
                }}
              >
                <div className="py-1">
                  <button
                    onClick={() => { onShowDetails(lab); handleClosePopover(); }}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left custom-text-[poppins-400-14px]"
                  >
                    <img src={require("assets/details-icon.svg").default} alt="" className="w-4 h-4 opacity-70" />
                    More details
                  </button>
                  <hr className="my-1 border-gray-200" />
                  <button
                    onClick={() => { onSendInvite(lab); handleClosePopover(); }}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left custom-text-[poppins-400-14px]"
                  >
                    <img src={require("assets/invite-icon.svg").default} alt="" className="w-4 h-4 opacity-70" />
                    Send Invite
                  </button>
                </div>
              </Popover>
            </div>
          </div>

          <p className="custom-text-[poppins-400-14px] text-gray-500 leading-relaxed mb-4 line-clamp-2">
            {lab.description || "No description available."}
          </p>

          <div className="flex flex-wrap gap-2 mt-auto">
            {lab.duration && <Tag icon="⏱️" text={`${lab.duration} min`} />}
            {lab.tags && lab.tags.length > 0 && <Tag icon="🏷️" text={lab.tags[0]} />}
            <Tag icon={rateTag.icon} text={rateTag.text} />
          </div>
        </div>
      </div>
    </>
  );
};


// --- Main Page Component ---
const OrgTestsPage = () => {
  const navigate = useNavigate();

  const { data: labs, isLoading, error } = useQuery({
    queryKey: ['qualification-labs'],
    queryFn: () => APIGetQualificationLabs().then(res => res.data),
  });

  const handleSendInviteClick = (lab: any) => {
    navigate(`/recruiter/invite/${lab.slug}`);
  };

  const handleShowDetailsClick = (lab: any) => {
    navigate(`/qualification-lab/${lab.slug}`);
  };

  return (
    <section className="flex w-full h-full border-l-[1px] border-[#F5F5F5] py-[56px]">
      <div className="flex flex-col pt-[56px] px-[46px] h-full w-full">
        {/* Header */}
        <div className="mb-6">
          <h2 className="custom-text-[volkhov-700-32px] text-gray-800">
            Qualification tests
          </h2>
          <hr className="mt-3 mb-6 border-gray-200" />
        </div>

        {/* Content Area */}
        {isLoading && <LoadingImage />}
        {error && <div className="text-red-500">Error loading labs: {(error as Error).message}</div>}
        {!isLoading && !error && labs && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {labs.map((lab) => (
              <QualificationCard
                key={lab.id}
                lab={lab}
                onSendInvite={handleSendInviteClick}
                onShowDetails={handleShowDetailsClick}
              />
            ))}
            {labs.length === 0 && (
              <p className="text-center text-gray-500 lg:col-span-2">No qualification tests found.</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default OrgTestsPage;